import { captureException } from '@sentry/nextjs';
import { ActionType } from 'src/modules/community/communityAbout/actionTypes';
import api, { createAuthenticatedApiInstance } from 'src/utils/api';
import { setAlert } from 'src/components/alert/actions';
import { userIsCommunityMemberOrHUStaff } from 'src/utils/context';
import { parseString } from 'src/utils/parserText';
import i18n from 'src/utils/translate';
import { LINEBREAKS } from 'src/constants/regex';

export const fetchCommunityAbout = (communitySlug: string) => {
  return async (dispatch, getState) => {
    dispatch({ type: ActionType.FETCH_COMMUNITY_ABOUT });
    try {
      const api = createAuthenticatedApiInstance(getState);
      const currentUser = getState().context.currentUser;
      const url = `communities/${communitySlug}/about`;
      const isCommunityMemberOrHUStaff = userIsCommunityMemberOrHUStaff({ currentUser, communitySlug });

      const { data: { description, supportingMembers, support, contact } } = !isCommunityMemberOrHUStaff
        ? await api.getPublic(url)
        : await api.get(`private/${url}`);

      dispatch({
        type: ActionType.FETCH_COMMUNITY_ABOUT_SUCCESS,
        description,
        enrichedDescription: parseString(description, []),
        supportingMembers,
        support,
        contact,
      });
    } catch (ex) {
      captureException(ex);
      dispatch({ type: ActionType.FETCH_COMMUNITY_ABOUT_FAILURE });
      dispatch(setAlert(ex.message));
    }
  };
};

type Props = {
  contact?: {
    contactNumber?: string;
    facebookPage?: string;
    instagramUsername?: string;
    twitterHandle?: string;
    website?: string;
    youtubePage?: string;
  };
  description?: string;
  support?: {
    supportBoxBody?: string;
    supportBoxHeading?: string;
    supportButtonText?: string;
    supportLink?: string;
  };
};

export const saveAboutData = (newData: Props) => {
  return async (dispatch, getState) => {
    dispatch({ type: ActionType.SAVE_ABOUT });
    try {
      const communitySlug = getState().context.currentCommunity?.slug;
      if (!communitySlug) {
        throw Error('saveAboutData: missing community-slug');
      }

      let trimmedDescription = newData?.description;
      if (trimmedDescription) {
        // Description can be deleted but the editors always have line breaks (1 for Android editor, 2 for the other editor),
        // hence we remove them before calling the endpoint
        if ((newData.description.length === 1 || newData.description.length === 2) && LINEBREAKS.test(newData.description)) {
          trimmedDescription = newData.description.replace(LINEBREAKS, ' ').trim();
        }
      }
      const { data, status } = await api.post(`private/communities/${communitySlug}/about`, {
        ...newData?.description && { description: trimmedDescription || null },
        ...newData?.contact && { contact: newData.contact },
        ...newData?.support && { support: newData.support },
      });

      if (status !== 200) {
        dispatch(setAlert());
        return dispatch({ type: ActionType.SAVE_ABOUT_FAILURE });
      }

      dispatch({
        type: ActionType.SAVE_ABOUT_SUCCESS,
        contact: data?.contact,
        description: data?.description,
        enrichedDescription: parseString(data?.description, []),
        support: data?.support,
      });
      return dispatch(setAlert(i18n.t('Community information updated'), 'success'));
    } catch (ex) {
      captureException(ex);
      dispatch(setAlert());
      return dispatch({ type: ActionType.SAVE_ABOUT_FAILURE });
    }
  };
};

export const setIsEditingDescription = () => async (dispatch) => {
  dispatch({ type: ActionType.SET_IS_EDITING });
};

export const setShortEnrichedDescription = (newDescription: string) => async (dispatch) => {
  dispatch({
    type: ActionType.SET_SHORT_ENRICHED_DESCRIPTION,
    enrichedDescriptionShort: parseString(newDescription, []),
  });
};
